import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Search, Filter, Eye, Edit, Calendar, User, Tag, AlertCircle, CheckCircle, Clock, XCircle, Trash2, ArrowLeft } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';

interface BugReport {
  _id: string;
  bugId: string;
  reportedBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  reporterEmail: string;
  reporterName: string;
  module: string;
  issueDescription: string;
  stepsToReproduce: string;
  priority: string;
  bugType: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  assignedTo?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  adminNotes?: string;
}

interface TicketFilters {
  search: string;
  status: string;
  module: string;
  priority: string;
  bugType: string;
  myTicketsOnly: boolean;
}

interface StatusUpdateData {
  status: string;
  adminNotes: string;
  assignedTo?: string;
}

export default function ViewTickets() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { success, error } = useToast();
  const [tickets, setTickets] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<BugReport | null>(null);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState<BugReport | null>(null);
  const [statusUpdateData, setStatusUpdateData] = useState<StatusUpdateData>({
    status: '',
    adminNotes: ''
  });
  const [editFormData, setEditFormData] = useState<{
    issueDescription?: string;
    stepsToReproduce?: string;
    module?: string;
    priority?: string;
    bugType?: string;
    status?: string;
    adminNotes?: string;
  }>({});
  const [filters, setFilters] = useState<TicketFilters>({
    search: '',
    status: 'all',
    module: 'all',
    priority: 'all',
    bugType: 'all',
    myTicketsOnly: false
  });

  const statusColors = {
    'Open': 'bg-red-100 text-red-800 border-red-200',
    'In Progress': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'Fixed': 'bg-primary-light text-green-800 border-green-200',
    'Closed': 'bg-gray-100 text-secondary border-border',
    'Rejected': 'bg-red-100 text-red-800 border-red-200'
  };

  const priorityColors = {
    'Low': 'bg-primary-light text-blue-800 border-blue-200',
    'Medium': 'bg-yellow-100 text-yellow-800 border-yellow-200',
    'High': 'bg-orange-100 text-orange-800 border-orange-200',
    'Critical': 'bg-red-100 text-red-800 border-red-200'
  };

  const bugTypeColors = {
    'Bug': 'bg-red-100 text-red-800 border-red-200',
    'Feature Request': 'bg-primary-light text-blue-800 border-blue-200',
    'Enhancement': 'bg-primary-light text-green-800 border-green-200',
    'UI/UX Issue': 'bg-purple-100 text-purple-800 border-purple-200',
    'Performance Issue': 'bg-orange-100 text-orange-800 border-orange-200'
  };

  const modules = [
    { value: 'all', label: 'All Modules' },
    { value: 'registration', label: 'Registration' },
    { value: 'login', label: 'Login' },
    { value: 'events', label: 'Events' },
    { value: 'jobs', label: 'Jobs' },
    { value: 'profile', label: 'Profile' },
    { value: 'dashboard', label: 'Dashboard' },
    { value: 'mentorship', label: 'Mentorship' },
    { value: 'admin_panel', label: 'Admin Panel' },
    { value: 'general', label: 'General' },
    { value: 'other', label: 'Other' }
  ];

  const statuses = [
    { value: 'all', label: 'All Statuses' },
    { value: 'Open', label: 'Open' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'Fixed', label: 'Fixed' },
    { value: 'Closed', label: 'Closed' },
    { value: 'Rejected', label: 'Rejected' }
  ];

  const priorities = [
    { value: 'all', label: 'All Priorities' },
    { value: 'Low', label: 'Low' },
    { value: 'Medium', label: 'Medium' },
    { value: 'High', label: 'High' },
    { value: 'Critical', label: 'Critical' }
  ];

  const bugTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'Bug', label: 'Bug' },
    { value: 'Feature Request', label: 'Feature Request' },
    { value: 'Enhancement', label: 'Enhancement' },
    { value: 'UI/UX Issue', label: 'UI/UX Issue' },
    { value: 'Performance Issue', label: 'Performance Issue' }
  ];

  useEffect(() => {
    if (isAuthenticated) {
      fetchTickets();
    }
  }, [filters, isAuthenticated]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.module && filters.module !== 'all') params.append('module', filters.module);
      if (filters.priority && filters.priority !== 'all') params.append('priority', filters.priority);
      if (filters.bugType && filters.bugType !== 'all') params.append('bugType', filters.bugType);

      // All users can see all tickets, but with different permissions
      const endpoint = filters.myTicketsOnly ? '/api/bug-reports/my-reports' : '/api/bug-reports/admin';
      
      const url = `${endpoint}?${params.toString()}`;
      
      const response = await fetch(url, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const ticketsData = data.data?.bugReports || data.data || [];
        setTickets(Array.isArray(ticketsData) ? ticketsData : []);
      } else {
        throw new Error(data.message || 'Failed to fetch tickets');
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      error('Error', `Failed to fetch tickets: ${errorMessage}`);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof TicketFilters, value: string | boolean) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      module: 'all',
      priority: 'all',
      bugType: 'all',
      myTicketsOnly: false
    });
  };

  const handleStatusUpdate = async () => {
    if (!selectedTicket || !statusUpdateData.status) return;

    try {
      const response = await fetch(`/api/bug-reports/${selectedTicket._id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(statusUpdateData)
      });

      const data = await response.json();

      if (data.success) {
        success('Status Updated', 'Ticket status updated successfully');
        setShowStatusDialog(false);
        setSelectedTicket(null);
        setStatusUpdateData({ status: '', adminNotes: '' });
        fetchTickets();
      } else {
        throw new Error(data.message || 'Failed to update status');
      }
    } catch (err) {
      console.error('Error updating status:', err);
      error('Error', 'Failed to update ticket status');
    }
  };

  const handleDeleteTicket = (ticket: BugReport) => {
    setTicketToDelete(ticket);
    setShowDeleteDialog(true);
  };

  const confirmDeleteTicket = async () => {
    if (!ticketToDelete) return;

    try {
      const response = await fetch(`/api/bug-reports/${ticketToDelete._id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        success('Ticket Deleted', `Ticket ${ticketToDelete.bugId} has been deleted successfully`);
        fetchTickets();
        setShowDeleteDialog(false);
        setTicketToDelete(null);
      } else {
        throw new Error(data.message || 'Failed to delete ticket');
      }
    } catch (err) {
      console.error('Error deleting ticket:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      error('Error', `Failed to delete ticket: ${errorMessage}`);
    } finally {
      setShowDeleteDialog(false);
      setTicketToDelete(null);
    }
  };

  const handleUpdateTicket = async () => {
    if (!selectedTicket) return;

    try {
      const updateData = user?.role === 'admin' ? {
        ...editFormData,
        adminNotes: editFormData.adminNotes
      } : {
        issueDescription: editFormData.issueDescription,
        stepsToReproduce: editFormData.stepsToReproduce,
        module: editFormData.module,
        priority: editFormData.priority,
        bugType: editFormData.bugType
      };

      const response = await fetch(`/api/bug-reports/${selectedTicket._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      const data = await response.json();

      if (data.success) {
        success('Ticket Updated', 'Ticket updated successfully');
        setShowEditDialog(false);
        setSelectedTicket(null);
        setEditFormData({});
        fetchTickets();
      } else {
        throw new Error(data.message || 'Failed to update ticket');
      }
    } catch (err) {
      console.error('Error updating ticket:', err);
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      error('Error', `Failed to update ticket: ${errorMessage}`);
    }
  };

  const canEditTicket = (ticket: BugReport) => {
    // Use email comparison since reportedBy._id might be undefined
    const isOwner = ticket.reporterEmail === user?.email;
    const canEdit = user?.role === 'admin' || isOwner;
    
    console.log('canEditTicket:', {
      canEdit,
      userRole: user?.role,
      isOwner,
      ticketReporterEmail: ticket.reporterEmail,
      currentUserEmail: user?.email
    });
    
    return canEdit;
  };

  const canDeleteTicket = (ticket: BugReport) => {
    // Use email comparison since reportedBy._id might be undefined
    const isOwner = ticket.reporterEmail === user?.email;
    const canDelete = user?.role === 'admin' || isOwner;
    
    console.log('canDeleteTicket:', {
      canDelete,
      userRole: user?.role,
      isOwner,
      ticketReporterEmail: ticket.reporterEmail,
      currentUserEmail: user?.email
    });
    
    return canDelete;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Open': return <AlertCircle className="w-4 h-4" />;
      case 'In Progress': return <Clock className="w-4 h-4" />;
      case 'Fixed': case 'Closed': return <CheckCircle className="w-4 h-4" />;
      case 'Rejected': return <XCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardContent className="text-center py-8">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Authentication Required</h2>
            <p className="text-gray-600">Please log in to view tickets.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-7xl mx-auto px-4">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4 mb-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/support')}
                className="text-gray-600 hover:text-secondary"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Support Center
              </Button>
            </div>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5 text-blue-500" />
              View Support Tickets
            </CardTitle>
            <p className="text-gray-600">
              {user?.role === 'admin' 
                ? 'Manage and track all support tickets in the system' 
                : 'View and track all support tickets. You can edit/delete your own tickets.'
              }
            </p>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="bg-background p-4 rounded-lg mb-6 relative">
              <div className="flex items-center gap-2 mb-4">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="font-medium text-gray-700">Filters</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={clearFilters}
                  className="ml-auto"
                >
                  Clear All
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                <div>
                  <Label htmlFor="search">Search</Label>
                  <Input
                    id="search"
                    placeholder="Search tickets..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="bg-surface"
                  />
                </div>

                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select value={filters.status} onValueChange={(value) => handleFilterChange('status', value)}>
                    <SelectTrigger className="bg-surface">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999] max-h-48 overflow-y-auto fixed bg-surface border shadow-lg">
                      {statuses.map(status => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="module">Module</Label>
                  <Select value={filters.module} onValueChange={(value) => handleFilterChange('module', value)}>
                    <SelectTrigger className="bg-surface">
                      <SelectValue placeholder="All Modules" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999] max-h-48 overflow-y-auto fixed bg-surface border shadow-lg">
                      {modules.map(module => (
                        <SelectItem key={module.value} value={module.value}>
                          {module.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="priority">Priority</Label>
                  <Select value={filters.priority} onValueChange={(value) => handleFilterChange('priority', value)}>
                    <SelectTrigger className="bg-surface">
                      <SelectValue placeholder="All Priorities" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999] max-h-48 overflow-y-auto fixed bg-surface border shadow-lg">
                      {priorities.map(priority => (
                        <SelectItem key={priority.value} value={priority.value}>
                          {priority.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="bugType">Type</Label>
                  <Select value={filters.bugType} onValueChange={(value) => handleFilterChange('bugType', value)}>
                    <SelectTrigger className="bg-surface">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent className="z-[9999] max-h-48 overflow-y-auto fixed bg-surface border shadow-lg">
                      {bugTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2 pt-6">
                  <input
                    type="checkbox"
                    id="myTicketsOnly"
                    checked={filters.myTicketsOnly}
                    onChange={(e) => handleFilterChange('myTicketsOnly', e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <Label htmlFor="myTicketsOnly">My Tickets Only</Label>
                </div>
              </div>
            </div>

            {/* Tickets List */}
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-gray-600">Loading tickets...</p>
              </div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-secondary mb-2">No tickets found</h3>
                <p className="text-gray-600">
                  {filters.search || 
                   (filters.status && filters.status !== 'all') || 
                   (filters.module && filters.module !== 'all') || 
                   (filters.priority && filters.priority !== 'all') || 
                   (filters.bugType && filters.bugType !== 'all')
                    ? 'Try adjusting your filters to see more results.'
                    : 'No tickets have been submitted yet.'
                  }
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {tickets.map((ticket) => (
                  <Card key={ticket._id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Badge variant="outline" className="font-mono text-xs">
                              {ticket.bugId}
                            </Badge>
                            <Badge className={statusColors[ticket.status as keyof typeof statusColors]}>
                              {getStatusIcon(ticket.status)}
                              <span className="ml-1">{ticket.status}</span>
                            </Badge>
                            <Badge className={priorityColors[ticket.priority as keyof typeof priorityColors]}>
                              {ticket.priority}
                            </Badge>
                            <Badge className={bugTypeColors[ticket.bugType as keyof typeof bugTypeColors]}>
                              {ticket.bugType}
                            </Badge>
                          </div>
                          
                          <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                            {ticket.issueDescription}
                          </h3>
                          
                          <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                            <div className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              <span>{ticket.reporterName}</span>
                              <span className="text-gray-400">({ticket.reporterEmail})</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Tag className="w-4 h-4" />
                              <span className="capitalize">{ticket.module}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              <span>{formatDate(ticket.createdAt)}</span>
                            </div>
                          </div>

                          {ticket.assignedTo && (
                            <div className="text-sm text-gray-600 mb-2">
                              <strong>Assigned to:</strong> {ticket.assignedTo.firstName} {ticket.assignedTo.lastName}
                            </div>
                          )}
                          
                          {ticket.adminNotes && (
                            <div className="bg-blue-50 p-3 rounded border border-blue-200 mb-3">
                              <p className="text-sm text-blue-800">
                                <strong>Admin Notes:</strong> {ticket.adminNotes}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex gap-2 ml-4">
                          {/* View Button */}
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" title="View Details">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-surface z-50">
                              <DialogHeader>
                                <DialogTitle>Ticket Details - {ticket.bugId}</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <Label className="font-semibold">Status</Label>
                                    <Badge className={`${statusColors[ticket.status as keyof typeof statusColors]} mt-1`}>
                                      {getStatusIcon(ticket.status)}
                                      <span className="ml-1">{ticket.status}</span>
                                    </Badge>
                                  </div>
                                  <div>
                                    <Label className="font-semibold">Priority</Label>
                                    <Badge className={`${priorityColors[ticket.priority as keyof typeof priorityColors]} mt-1`}>
                                      {ticket.priority}
                                    </Badge>
                                  </div>
                                  <div>
                                    <Label className="font-semibold">Type</Label>
                                    <Badge className={`${bugTypeColors[ticket.bugType as keyof typeof bugTypeColors]} mt-1`}>
                                      {ticket.bugType}
                                    </Badge>
                                  </div>
                                  <div>
                                    <Label className="font-semibold">Module</Label>
                                    <p className="capitalize">{ticket.module}</p>
                                  </div>
                                </div>
                                
                                <div>
                                  <Label className="font-semibold">Reporter</Label>
                                  <p>{ticket.reporterName} ({ticket.reporterEmail})</p>
                                </div>
                                
                                <div>
                                  <Label className="font-semibold">Issue Description</Label>
                                  <p className="whitespace-pre-wrap bg-background p-3 rounded border">
                                    {ticket.issueDescription}
                                  </p>
                                </div>
                                
                                <div>
                                  <Label className="font-semibold">Steps to Reproduce</Label>
                                  <p className="whitespace-pre-wrap bg-background p-3 rounded border">
                                    {ticket.stepsToReproduce}
                                  </p>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <Label className="font-semibold">Created</Label>
                                    <p>{formatDate(ticket.createdAt)}</p>
                                  </div>
                                  <div>
                                    <Label className="font-semibold">Last Updated</Label>
                                    <p>{formatDate(ticket.updatedAt)}</p>
                                  </div>
                                </div>
                                
                                {ticket.assignedTo && (
                                  <div>
                                    <Label className="font-semibold">Assigned To</Label>
                                    <p>{ticket.assignedTo.firstName} {ticket.assignedTo.lastName}</p>
                                  </div>
                                )}
                                
                                {ticket.adminNotes && (
                                  <div>
                                    <Label className="font-semibold">Admin Notes</Label>
                                    <p className="bg-blue-50 p-3 rounded border border-blue-200">
                                      {ticket.adminNotes}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </DialogContent>
                          </Dialog>
                          
                          {/* Edit Button - Admins can edit any ticket, users can edit their own */}
                          {canEditTicket(ticket) && (
                            <Button
                              variant="outline"
                              size="sm"
                              title={user?.role === 'admin' ? 'Edit Status & Notes' : 'Edit Ticket'}
                              onClick={() => {
                                setSelectedTicket(ticket);
                                setEditFormData({
                                  issueDescription: ticket.issueDescription,
                                  stepsToReproduce: ticket.stepsToReproduce,
                                  module: ticket.module,
                                  priority: ticket.priority,
                                  bugType: ticket.bugType,
                                  status: ticket.status,
                                  adminNotes: ticket.adminNotes || ''
                                });
                                setShowEditDialog(true);
                              }}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}

                          {/* Delete Button - Admins can delete any ticket, users can delete their own */}
                          {canDeleteTicket(ticket) && (
                            <Button
                              variant="outline"
                              size="sm"
                              title="Delete Ticket"
                              onClick={() => handleDeleteTicket(ticket)}
                              className="hover:bg-red-50 hover:border-red-300"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Edit Ticket Dialog */}
            <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
              <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-surface z-50">
                <DialogHeader>
                  <DialogTitle>Edit Ticket - {selectedTicket?.bugId}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="editIssueDescription">Issue Description</Label>
                    <Textarea
                      id="editIssueDescription"
                      value={editFormData.issueDescription || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, issueDescription: e.target.value }))}
                      rows={4}
                      disabled={user?.role !== 'admin' && selectedTicket?.reporterEmail !== user?.email}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="editStepsToReproduce">Steps to Reproduce</Label>
                    <Textarea
                      id="editStepsToReproduce"
                      value={editFormData.stepsToReproduce || ''}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, stepsToReproduce: e.target.value }))}
                      rows={3}
                      disabled={user?.role !== 'admin' && selectedTicket?.reporterEmail !== user?.email}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="editModule">Module</Label>
                      <Select 
                        value={editFormData.module || ''} 
                        onValueChange={(value) => setEditFormData(prev => ({ ...prev, module: value }))}
                        disabled={user?.role !== 'admin' && selectedTicket?.reporterEmail !== user?.email}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select module" />
                        </SelectTrigger>
                        <SelectContent className="z-[10000] bg-surface border shadow-lg max-h-48 overflow-y-auto">
                          {modules.filter(m => m.value !== 'all').map(module => (
                            <SelectItem key={module.value} value={module.value}>
                              {module.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="editPriority">Priority</Label>
                      <Select 
                        value={editFormData.priority || ''} 
                        onValueChange={(value) => setEditFormData(prev => ({ ...prev, priority: value }))}
                        disabled={user?.role !== 'admin' && selectedTicket?.reporterEmail !== user?.email}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                        <SelectContent className="z-[10000] bg-surface border shadow-lg max-h-48 overflow-y-auto">
                          {priorities.filter(p => p.value !== 'all').map(priority => (
                            <SelectItem key={priority.value} value={priority.value}>
                              {priority.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="editBugType">Bug Type</Label>
                    <Select 
                      value={editFormData.bugType || ''} 
                      onValueChange={(value) => setEditFormData(prev => ({ ...prev, bugType: value }))}
                      disabled={user?.role !== 'admin' && selectedTicket?.reporterEmail !== user?.email}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="z-[10000] bg-surface border shadow-lg max-h-48 overflow-y-auto">
                        {bugTypes.filter(t => t.value !== 'all').map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Only admins can change status */}
                  {user?.role === 'admin' && (
                    <>
                      <div>
                        <Label htmlFor="editStatus">Status (Admin Only)</Label>
                        <Select 
                          value={editFormData.status || ''} 
                          onValueChange={(value) => setEditFormData(prev => ({ ...prev, status: value }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent className="z-[10000] bg-surface border shadow-lg max-h-48 overflow-y-auto">
                            {statuses.filter(s => s.value !== 'all').map(status => (
                              <SelectItem key={status.value} value={status.value}>
                                {status.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label htmlFor="editAdminNotes">Admin Notes</Label>
                        <Textarea
                          id="editAdminNotes"
                          value={editFormData.adminNotes || ''}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, adminNotes: e.target.value }))}
                          rows={3}
                          placeholder="Add admin notes..."
                        />
                      </div>
                    </>
                  )}
                  
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={() => handleUpdateTicket()}>
                      Update Ticket
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Status Update Dialog */}
            <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
              <DialogContent className="bg-surface z-50">
                <DialogHeader>
                  <DialogTitle>Update Ticket Status</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select 
                      value={statusUpdateData.status} 
                      onValueChange={(value) => setStatusUpdateData(prev => ({ ...prev, status: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[10000] bg-surface border shadow-lg max-h-48 overflow-y-auto">
                        <SelectItem value="Open">Open</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Fixed">Fixed</SelectItem>
                        <SelectItem value="Closed">Closed</SelectItem>
                        <SelectItem value="Rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="adminNotes">Admin Notes</Label>
                    <Textarea
                      id="adminNotes"
                      placeholder="Add notes about this status change..."
                      value={statusUpdateData.adminNotes}
                      onChange={(e) => setStatusUpdateData(prev => ({ ...prev, adminNotes: e.target.value }))}
                      rows={3}
                    />
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleStatusUpdate}>
                      Update Status
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      </div>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>Are you sure you want to delete ticket <strong>{ticketToDelete?.bugId}</strong>?</p>
            <p className="text-sm text-gray-600 mt-2">This action cannot be undone.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDeleteTicket}>
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
} 